import { useEffect, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import { FileSearch } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Card } from 'src/shared/components/ui/card'
import {
  APPROVED_QUOTE_STATUS,
  DELIVERED_STATUS,
  SALES_APPROVED_QUOTE_STATUS,
  STARTED_PRODUCTION_STATUS,
  effectiveQuoteStatus,
  isPreSalesApprovalStatus,
  SHIPPING_QUOTE_STATUS,
} from 'src/shared/consts/quoteStatus'
import { useIsAdmin } from 'src/shared/hooks/useIsAdmin'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import { getQuotePreviewThumbnails } from 'src/shared/utils/quoteItemThumbnail'
import { proofEntriesFromQuoteProofLinks } from 'src/shared/utils/proofUrls'
import { useRequestContext } from '../../../hooks/RequestContext'
import { VerifyProofs } from '../VerifyProofs'
import { ApproveQuoteStep } from './components/ApproveQuoteStep'
import {
  checkoutMoodCopy,
  getCheckoutMood,
} from './components/CheckoutHero'
import {
  CheckoutStep,
  CheckoutStepConnector,
  ProductThumbs,
} from './components/CheckoutStep'
import { ConfirmOrderStep } from './components/ConfirmOrderStep'
import { useCheckoutDetails } from './hooks'

type StepId = 'approve_quote_and_proofs' | 'confirm_order'

const STEP_ORDER: StepId[] = ['approve_quote_and_proofs', 'confirm_order']

const isCheckoutFullyComplete = (status: string) =>
  status === STARTED_PRODUCTION_STATUS ||
  status === SHIPPING_QUOTE_STATUS ||
  status === DELIVERED_STATUS

const getActiveStep = (status: string): StepId | null => {
  if (isCheckoutFullyComplete(status)) return null
  if (
    status === APPROVED_QUOTE_STATUS ||
    isPreSalesApprovalStatus(status) ||
    status === SALES_APPROVED_QUOTE_STATUS
  ) {
    return 'approve_quote_and_proofs'
  }
  return 'confirm_order'
}

const getStepState = (
  stepId: StepId,
  status: string
): 'locked' | 'active' | 'completed' => {
  if (isCheckoutFullyComplete(status)) return 'completed'

  const active = getActiveStep(status)
  if (!active) return 'completed'

  const stepIndex = STEP_ORDER.indexOf(stepId)
  const activeIndex = STEP_ORDER.indexOf(active)

  if (stepIndex < activeIndex) return 'completed'
  if (stepIndex === activeIndex) return 'active'
  return 'locked'
}

const panelTransition = {
  duration: 0.32,
  ease: [0.22, 1, 0.36, 1] as const,
}

export const Checkout = () => {
  const { t } = useLocalization()
  const isAdmin = useIsAdmin()
  const { quote, quoteItems } = useRequestContext()
  const status = effectiveQuoteStatus(quote?.status)
  const activeStep = getActiveStep(status)
  const [openStep, setOpenStep] = useState<StepId | null>(activeStep)
  const checkoutDetails = useCheckoutDetails()
  const hasProofs =
    proofEntriesFromQuoteProofLinks(quote?.quote_proof).length > 0
  const mood = getCheckoutMood(status, hasProofs)
  const copy = checkoutMoodCopy(mood, isAdmin, t)
  const thumbs = getQuotePreviewThumbnails(quoteItems, 4)
    .previews.filter((preview) => preview.url)
    .map((preview) => ({ key: preview.key, url: preview.url as string }))

  useEffect(() => {
    setOpenStep(activeStep ?? 'confirm_order')
  }, [activeStep])

  const selectStep = (stepId: StepId) => {
    if (getStepState(stepId, status) === 'locked') return
    setOpenStep(stepId)
  }

  const approvalState = getStepState('approve_quote_and_proofs', status)
  const confirmState = getStepState('confirm_order', status)

  const confirmCopy = checkoutMoodCopy('confirm_order', isAdmin, t)

  const approvalStep =
    approvalState === 'completed'
      ? {
          title: t(
            'Quote and proofs approved',
            'Offert och korrekturer godkända'
          ),
          description: t(
            'Pricing, sizes, colors, and factory proofs are approved.',
            'Pris, storlekar, färger och korrekturer är godkända.'
          ),
        }
      : {
          title: copy.title,
          description: copy.body,
        }

  const confirmStep =
    confirmState === 'completed'
      ? {
          title: copy.title,
          description: copy.body,
        }
      : {
          title: confirmCopy.title,
          description: confirmCopy.body,
        }

  return (
    <Column className="w-full gap-5 pb-8">
      <Row className="items-stretch gap-2">
        <CheckoutStep
          stepNumber={1}
          title={approvalStep.title}
          description={approvalStep.description}
          state={approvalState}
          selected={openStep === 'approve_quote_and_proofs'}
          onSelect={() => selectStep('approve_quote_and_proofs')}
          accessory={
            approvalState !== 'completed' && thumbs.length > 0 ? (
              <ProductThumbs items={thumbs} />
            ) : undefined
          }
        />
        <CheckoutStepConnector complete={approvalState === 'completed'} />
        <CheckoutStep
          stepNumber={2}
          title={confirmStep.title}
          description={confirmStep.description}
          state={confirmState}
          selected={openStep === 'confirm_order'}
          onSelect={() => selectStep('confirm_order')}
        />
      </Row>

      <AnimatePresence mode="wait">
        {openStep === 'approve_quote_and_proofs' ? (
          <motion.div
            key="approve_quote_and_proofs"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={panelTransition}
          >
            <Column className="gap-3">
              <Card wrapperClassName="!shadow-sm" className="gap-4 p-5 !shadow-none">
                <ApproveQuoteStep />
              </Card>
              <Card wrapperClassName="!shadow-sm" className="gap-3 p-5 !shadow-none">
                <Row className="items-center gap-2">
                  <FileSearch className="h-4 w-4 text-muted-foreground" />
                  <h3>{t('Factory proofs', 'Korrekturer')}</h3>
                </Row>
                <VerifyProofs quote={quote} embedded />
              </Card>
            </Column>
          </motion.div>
        ) : null}

        {openStep === 'confirm_order' ? (
          <motion.div
            key="confirm_order"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={panelTransition}
          >
            <Card wrapperClassName="!shadow-sm" className="gap-6 p-5 !shadow-none">
              <ConfirmOrderStep
                quote={checkoutDetails.quote}
                selectedLocationIds={checkoutDetails.selectedLocationIds}
                onToggleLocation={(id) =>
                  checkoutDetails.setSelectedLocationIds((prev) =>
                    prev.includes(id)
                      ? prev.filter((x) => x !== id)
                      : [...prev, id]
                  )
                }
                locationNotesByShippingLocationId={
                  checkoutDetails.locationNotesByShippingLocationId
                }
                onLocationNotesChange={
                  checkoutDetails.setLocationNotesByShippingLocationId
                }
                onShippingLocationDeleted={(locationId) =>
                  checkoutDetails.setSelectedLocationIds((prev) =>
                    prev.filter((x) => x !== locationId)
                  )
                }
                onBillingReadyChange={checkoutDetails.setBillingFormReady}
                detailsReady={checkoutDetails.canPlaceOrder}
                onConfirm={checkoutDetails.handlePlaceOrder}
                isLoading={checkoutDetails.isStartingProduction}
              />
            </Card>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </Column>
  )
}
